/**
 * ScreenerFilters: the run bar's Filters panel, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3,
 * 2026-10-28). Blank inputs send null so entry_signals.json decides; the request is built in ScreenView, not here.
 */
import React from "react";
import { inputStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { configPlaceholder, currency, type ConfigOverrideForm } from "@lib/screenerTruth";
import { HelionButton } from "@components/ui/HelionButton";
import { describeBudgetRole, type ScreenerBudget } from "@lib/screenerBudget";
import { type HarvestLockout } from "@lib/harvestLockout";
import { type ScreenerRequest } from "@lib/screenerTypes";

export type ScreenerFiltersProps = {
  bandPct: string;
  budgetTally: ScreenerBudget;
  canSubmit: boolean;
  capital: string;
  configHint: (value: number | string | null, suffix?: string) => string;
  currentVega: string;
  earliestMonthsAhead: string;
  expirationCapPct: string;
  harvestLockout: HarvestLockout;
  minDistinctExpiries: string;
  minDte: string;
  minOI: string;
  moneynessBufferPct: string;
  monthsLimit: string;
  oiCap100to499: string;
  oiCap1500plus: string;
  oiCap500to1499: string;
  oiCapUnder100: string;
  resolvedConfig: Record<keyof ConfigOverrideForm, any>;
  resolvedEntryConfig: { minDte: number | null; minOpenInterest: number | null; minOiForSizing: number | null; capUnder100: string | null; cap100to499: string | null; cap500to1499: string | null; cap1500plus: string | null; };
  right: "C" | "P";
  runScreener: (e?: React.FormEvent, overrides?: Partial<ScreenerRequest>) => Promise<void>;
  setBandPct: React.Dispatch<React.SetStateAction<string>>;
  setCapital: React.Dispatch<React.SetStateAction<string>>;
  setCurrentVega: React.Dispatch<React.SetStateAction<string>>;
  setEarliestMonthsAhead: React.Dispatch<React.SetStateAction<string>>;
  setExpirationCapPct: React.Dispatch<React.SetStateAction<string>>;
  setMinDistinctExpiries: React.Dispatch<React.SetStateAction<string>>;
  setMinDte: React.Dispatch<React.SetStateAction<string>>;
  setMinOI: React.Dispatch<React.SetStateAction<string>>;
  setMoneynessBufferPct: React.Dispatch<React.SetStateAction<string>>;
  setMonthsLimit: React.Dispatch<React.SetStateAction<string>>;
  setOiCap100to499: React.Dispatch<React.SetStateAction<string>>;
  setOiCap1500plus: React.Dispatch<React.SetStateAction<string>>;
  setOiCap500to1499: React.Dispatch<React.SetStateAction<string>>;
  setOiCapUnder100: React.Dispatch<React.SetStateAction<string>>;
  setRight: React.Dispatch<React.SetStateAction<"C" | "P">>;
  setSymbol: React.Dispatch<React.SetStateAction<string>>;
  symbol: string;
};

export default function ScreenerFilters({
  bandPct,
  budgetTally,
  canSubmit,
  capital,
  configHint,
  currentVega,
  earliestMonthsAhead,
  expirationCapPct,
  harvestLockout,
  minDistinctExpiries,
  minDte,
  minOI,
  moneynessBufferPct,
  monthsLimit,
  oiCap100to499,
  oiCap1500plus,
  oiCap500to1499,
  oiCapUnder100,
  resolvedConfig,
  resolvedEntryConfig,
  right,
  runScreener,
  setBandPct,
  setCapital,
  setCurrentVega,
  setEarliestMonthsAhead,
  setExpirationCapPct,
  setMinDistinctExpiries,
  setMinDte,
  setMinOI,
  setMoneynessBufferPct,
  setMonthsLimit,
  setOiCap100to499,
  setOiCap1500plus,
  setOiCap500to1499,
  setOiCapUnder100,
  setRight,
  setSymbol,
  symbol,
}: ScreenerFiltersProps) {
  return (
    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="screener-filters">
      <div className="rounded-2xl border p-4" style={{ borderColor: ui.cardBorder }}>
        <div className="mb-3 text-sm font-semibold" style={secondaryText}>
          Contract filters
        </div>
          <label htmlFor="minDte" className="mb-1 block text-sm">
            Min DTE
          </label>
          <input
            id="minDte"
            type="number"
            min={0}
            step="1"
            value={minDte}
            onChange={(e) => setMinDte(e.target.value)}
            style={inputStyle()}
            placeholder={configHint(resolvedEntryConfig.minDte)}
          />
          <div className="mt-1 text-xs" style={mutedText}>
            Leave blank to use <code>entry_signals.json</code> (the source of truth). Typing a
            value overrides it for this run only.
          </div>

          <label htmlFor="earliestMonthsAhead" className="mb-1 mt-3 block text-sm">
            Earliest Months Ahead (LEAPS)
          </label>
          <input
            id="earliestMonthsAhead"
            type="number"
            min={0}
            step="1"
            value={earliestMonthsAhead}
            onChange={(e) => setEarliestMonthsAhead(e.target.value)}
            style={inputStyle()}
            placeholder="e.g., 24"
          />

          <label htmlFor="bandPct" className="mb-1 mt-3 block text-sm">
            Moneyness Band (±, fraction)
          </label>
          <input
            id="bandPct"
            type="number"
            min={0}
            step="0.01"
            value={bandPct}
            onChange={(e) => setBandPct(e.target.value)}
            style={inputStyle()}
            placeholder={configPlaceholder(resolvedConfig.bandPct)}
          />
          <div className="mt-1 text-xs" style={mutedText}>
            Base band. The run also applies a tiny edge buffer so discrete strike grids do not flicker in/out by a few index points.
          </div>

          <label htmlFor="moneynessBufferPct" className="mb-1 mt-3 block text-sm">
            Moneyness Edge Buffer
          </label>
          <input
            id="moneynessBufferPct"
            type="number"
            min={0}
            step="0.0005"
            value={moneynessBufferPct}
            onChange={(e) => setMoneynessBufferPct(e.target.value)}
            style={inputStyle()}
            placeholder={configPlaceholder(resolvedConfig.moneynessBufferPct)}
          />

          <label htmlFor="minOI" className="mb-1 mt-3 block text-sm">
            Min Open Interest
          </label>
          <input
            id="minOI"
            type="number"
            min={0}
            step="1"
            value={minOI}
            onChange={(e) => setMinOI(e.target.value)}
            style={inputStyle()}
            placeholder={configHint(resolvedEntryConfig.minOpenInterest)}
          />
          <div className="mt-1 text-xs" style={mutedText}>
            Leave blank to use <code>entry_signals.json</code>. This is a <strong>hard filter</strong>:
            a line below it is dropped from the screen, not sized down. OI 50–99 is treated as
            starter liquidity and sizing is capped tightly; missing API OI is ranked for review
            but must be verified in TWS.
          </div>

          <label htmlFor="monthsLimit" className="mb-1 mt-3 block text-sm">
            Expiry Scope / Max Expiries
          </label>
          <input
            id="monthsLimit"
            type="number"
            min={0}
            step="1"
            value={monthsLimit}
            onChange={(e) => setMonthsLimit(e.target.value)}
            style={inputStyle()}
          />
          <div className="mt-1 text-xs" style={mutedText}>
            0 means scan every DTE-qualified expiry returned by IBKR. Use a positive number only for intentional debugging caps.
          </div>
      </div>

      <div className="rounded-2xl border p-4" style={{ borderColor: ui.cardBorder }}>
        <div className="mb-3 text-sm font-semibold" style={secondaryText}>
          Scope &amp; OI sizing caps
        </div>
        <div className="grid grid-cols-2 gap-3">
      <div className="md:col-span-1">
        <label htmlFor="symbol" className="mb-1 block text-sm">
          Symbol
        </label>
        <input
          id="symbol"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          style={inputStyle()}
          autoComplete="off"
        />
      </div>
      <div className="md:col-span-1">
        <label htmlFor="right" className="mb-1 block text-sm">
          Right
        </label>
        <select
          id="right"
          value={right}
          onChange={(e) => setRight(e.target.value as "C" | "P")}
          style={inputStyle()}
        >
          <option value="C">Call</option>
          <option value="P">Put</option>
        </select>
      </div>
        </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="oiCapUnder100" className="mb-1 block text-xs">
                OI 50–99 starter cap
              </label>
              <input
                id="oiCapUnder100"
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={oiCapUnder100}
                onChange={(e) => setOiCapUnder100(e.target.value)}
                style={inputStyle()}
                placeholder={configHint(resolvedEntryConfig.capUnder100)}
              />
            </div>
            <div>
              <label htmlFor="oiCap100to499" className="mb-1 block text-xs">
                100–499 (cap)
              </label>
              <input
                id="oiCap100to499"
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={oiCap100to499}
                onChange={(e) => setOiCap100to499(e.target.value)}
                style={inputStyle()}
                placeholder={configHint(resolvedEntryConfig.cap100to499)}
              />
            </div>
            <div>
              <label htmlFor="oiCap500to1499" className="mb-1 block text-xs">
                500–1499 (cap)
              </label>
              <input
                id="oiCap500to1499"
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={oiCap500to1499}
                onChange={(e) => setOiCap500to1499(e.target.value)}
                style={inputStyle()}
                placeholder={configHint(resolvedEntryConfig.cap500to1499)}
              />
            </div>
            <div>
              <label htmlFor="oiCap1500plus" className="mb-1 block text-xs">
                ≥1500 (cap)
              </label>
              <input
                id="oiCap1500plus"
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={oiCap1500plus}
                onChange={(e) => setOiCap1500plus(e.target.value)}
                style={inputStyle()}
                placeholder={configHint(resolvedEntryConfig.cap1500plus)}
              />
            </div>
          </div>
          <div className="mt-2 text-xs" style={mutedText}>
            Share-of-line sizing caps: max contracts on a line ={" "}
            <code>floor(line OI × tier %)</code>. Leave a tier blank to use{" "}
            <code>entry_signals.json</code>; only the tiers you type are sent.
            {resolvedEntryConfig.minOiForSizing != null && (
              <>
                {" "}A line with OI below{" "}
                <strong>{resolvedEntryConfig.minOiForSizing}</strong> sizes to zero regardless
                of tier (<code>oi_caps.min_oi_for_sizing</code>).
              </>
            )}{" "}
            <strong>These are advisory in market-ranker mode</strong> — the allocator does not
            run, so they inform the Sizing Max column you size from rather than binding a
            quantity.
          </div>
      </div>

      <div className="rounded-2xl border p-4" style={{ borderColor: ui.cardBorder }}>
        <div className="mb-3 text-sm font-semibold" style={secondaryText}>
          Ladder &amp; breadth
        </div>
          <label htmlFor="expirationCapPct" className="mb-1 block text-sm">
            Per-Expiry Notional Cap
          </label>
          <input
            id="expirationCapPct"
            type="number"
            min={0}
            max={1}
            step="0.01"
            value={expirationCapPct}
            onChange={(e) => setExpirationCapPct(e.target.value)}
            style={inputStyle()}
            placeholder={configPlaceholder(resolvedConfig.expirationCapPct)}
          />
        <div className="mt-1 text-xs" style={mutedText}>
          Feeds the ladder&rsquo;s &ldquo;full&rdquo; test (shown as the per-expiry cap on the ladder panel).
        </div>
          <label htmlFor="minDistinctExpiries" className="mb-1 mt-3 block text-sm">
            Min Distinct Expiries
          </label>
          <input
            id="minDistinctExpiries"
            type="number"
            min={1}
            step="1"
            value={minDistinctExpiries}
            onChange={(e) => setMinDistinctExpiries(e.target.value)}
            style={inputStyle()}
            placeholder={configPlaceholder(resolvedConfig.minDistinctExpiries)}
          />
        <div className="mt-1 text-xs" style={mutedText}>
          The breadth target in Diagnostics &rarr; Market-Rank Integrity. Ranks nothing.
        </div>
        <div className="mt-4 border-t pt-3" style={{ borderColor: ui.cardBorder }}>
          <HelionButton
            onClick={(ev: any) => runScreener(ev, { force_quote_rehydration: true, quote_hydration_enabled: true, quote_hydration_max_attempts: 5 })}
            disabled={!canSubmit}
            variant="secondary"
            type="button"
          >
            Rehydrate Quotes
          </HelionButton>
          <div className="mt-1 text-xs" style={mutedText}>
            Re-runs the screen with 5 quote-retry attempts instead of the config default. Otherwise the same request.
          </div>
        </div>
      </div>

      <div className="rounded-2xl border p-4" style={{ borderColor: ui.cardBorder }}>
        <div className="mb-3 text-sm font-semibold" style={secondaryText}>
          Sizing tally &amp; reference
        </div>
        <div className="space-y-3">
      <div className="md:col-span-1">
        <label htmlFor="capital" className="mb-1 block text-sm">
          Optional Sizing Budget (USD)
        </label>
        <input
          id="capital"
          type="number"
          min={0}
          step="1"
          value={capital}
          onChange={(e) => setCapital(e.target.value)}
          style={inputStyle()}
        />
        {/* The figure typed above is a CIO input the app never reduces, so the
            lockout is STATED here rather than silently netted out of what he typed.
            This page reads /home_run_harvest only, which carries no rebalance-engine
            fields — so the engine's own buy-block sentence is not available here.
            It renders on the Risk dashboard's Book Stance block, which receives the
            engine meta. */}
        {/* WHAT THIS FIELD DOES, stated plainly (2026-09-17). It previously said only
            that deployment dollars are not used "as a hard filter", which reads as
            "used, but softly". Until today it was used for NOTHING: the page always
            sends market_rank_only, so screener_core zeroes the budget and no allocation
            loop runs. It now drives the running total on the ranked-contracts table. */}
        <div className="mt-1 text-[11px]" style={mutedText}>
          {describeBudgetRole(budgetTally.budget)}
        </div>
        <div className="mt-1 text-[11px]" style={mutedText}>
          {!harvestLockout.known
            ? "Harvest lockout unknown — this budget cannot be confirmed free of locked capital."
            : harvestLockout.status === "withheld"
            ? `${currency(harvestLockout.withheldFromBuyBudget)} is withheld by a harvest lockout and is NOT part of this budget.`
            : harvestLockout.status === "locked_not_applied"
            ? `${currency(harvestLockout.rawLockedNotional)} is under a harvest lockout, though policy does not subtract it from the buy budget.`
            : "No harvest lockout is withholding capital."}
        </div>
      </div>
      <div className="md:col-span-1">
        <label htmlFor="currentVega" className="mb-1 block text-sm">
          Current Vega (reference only)
        </label>
        <input
          id="currentVega"
          type="number"
          step="1"
          value={currentVega}
          onChange={(e) => setCurrentVega(e.target.value)}
          style={inputStyle()}
        />
      </div>
        </div>
      </div>
    </div>
  );
}
